import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  Sparkles,
  Search,
  Star,
  Wallet,
  Bell,
  Award,
  Filter,
  ArrowRight,
  ArrowLeft,
  Check,
} from "lucide-react";
import { useOnboarding } from "../context/OnboardingContext";

const STEPS = [
  {
    icon: Sparkles,
    title: "Welcome to UpTick",
    body:
      "UpTick helps beginner traders chart stocks and crypto with AI-powered explanations. Let's take a 60-second tour of what you can do.",
    accent: "#D18E63",
  },
  {
    icon: Search,
    title: "Find anything fast",
    body:
      "Hit ⌘K (or click the search bar) anywhere to instantly look up any stock or crypto. Try AAPL, NVDA, BTC — or a name like Tesla.",
    accent: "#547A8F",
  },
  {
    icon: Star,
    title: "Build watchlists",
    body:
      "Save assets you care about. Multiple watchlists are supported — group by theme (e.g. AI, Renewables) or risk level. Daily movers will spotlight the biggest gainers from your lists each session.",
    accent: "#5C8D73",
  },
  {
    icon: Wallet,
    title: "Practice with paper trading",
    body:
      "Every account starts with $10,000 of virtual cash. Buy and sell freely to learn how prices, position sizing, and P&L feel — without real risk. Your equity curve charts your progress vs. SPY.",
    accent: "#D18E63",
  },
  {
    icon: Bell,
    title: "Set price alerts",
    body:
      "On any asset, click 'Set alert' to get a ping when prices, percent changes, or RSI hit your target. We poll every minute and ring the bell in the navbar.",
    accent: "#C66B59",
  },
  {
    icon: Award,
    title: "CAN SLIM scoring",
    body:
      "Every US stock gets a CAN SLIM grade (A/B/C/D) based on William O'Neil's 7-pillar growth-stock methodology. Use it to spot leaders before the crowd.",
    accent: "#547A8F",
  },
  {
    icon: Filter,
    title: "Discover with the screener",
    body:
      "The Screener page lets you filter the entire US market by sector, market cap, P/E, performance, and more. Great for finding fresh ideas.",
    accent: "#2A2F2B",
  },
];

export default function OnboardingTour() {
  const { open, close } = useOnboarding();
  const [step, setStep] = useState(0);

  const reset = () => setStep(0);
  const isLast = step === STEPS.length - 1;
  const handleClose = () => {
    close();
    reset();
  };

  if (!open) return null;
  const s = STEPS[step];
  const Icon = s.icon;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
      }}
    >
      <DialogContent
        className="rounded-2xl border-[#EAE6E1] max-w-md p-0 overflow-hidden"
        data-testid="onboarding-dialog"
      >
        {/* Top accent bar */}
        <div
          className="h-1.5 w-full"
          style={{ backgroundColor: s.accent }}
          aria-hidden="true"
        />
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: `${s.accent}18` }}
            >
              <Icon className="w-6 h-6" style={{ color: s.accent }} strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
                Step {step + 1} of {STEPS.length}
              </div>
              <DialogTitle className="font-heading text-2xl tracking-tight text-[#2A2F2B]">
                {s.title}
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-sm text-[#6B7068] leading-relaxed mb-6">
            {s.body}
          </DialogDescription>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mb-5" data-testid="onboarding-progress">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                aria-label={`Go to step ${i + 1}`}
                className={`h-1.5 rounded-full transition-soft ${
                  i === step ? "w-6 bg-[#2A2F2B]" : "w-1.5 bg-[#EAE6E1] hover:bg-[#6B7068]"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              onClick={handleClose}
              data-testid="onboarding-skip-btn"
              className="text-sm text-[#6B7068] rounded-full"
            >
              Skip tour
            </Button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setStep((s) => s - 1)}
                  data-testid="onboarding-prev-btn"
                  className="rounded-full bg-white border-[#EAE6E1]"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} /> Back
                </Button>
              )}
              <Button
                onClick={() => (isLast ? handleClose() : setStep((s) => s + 1))}
                data-testid={isLast ? "onboarding-finish-btn" : "onboarding-next-btn"}
                className="rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white"
              >
                {isLast ? (
                  <>
                    <Check className="w-4 h-4 mr-1" strokeWidth={1.75} /> Got it
                  </>
                ) : (
                  <>
                    Next <ArrowRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
